/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

const EMPTY = '—';

// In it-IT l'ICU non raggruppa i numeri a quattro cifre ("1234,50 €"):
// `useGrouping: 'always'` dà "1.234,50 €" anche sotto le 5 cifre.
const euroFormat = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  useGrouping: 'always',
} as unknown as Intl.NumberFormatOptions);

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Importo in euro all'italiana. Intl separa il simbolo con uno spazio
 * non divisibile (NBSP): lo normalizziamo a spazio semplice, così il testo
 * mostrato e quello dei test coincidono.
 */
export const eur = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY;
  // Number('  ') vale 0: una stringa di soli spazi è un importo mancante.
  const text = typeof value === 'number' ? value : value.trim();
  if (text === '') return EMPTY;
  const number = typeof text === 'number' ? text : Number(text);
  if (!Number.isFinite(number)) return EMPTY;
  return euroFormat.format(number).replace(/\u00a0/g, ' ');
};

/** Data `YYYY-MM-DD` (o ISO completa) come `dd/MM/yyyy`, senza passare dai fusi. */
export const day = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : EMPTY;
};

/** Istante ISO come `dd/MM/yyyy HH:mm`, nell'ora locale del browser. */
export const instant = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return EMPTY;
  return (
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

export const personName = (
  p: {firstName?: string; lastName?: string} | null | undefined,
): string => {
  if (!p) return EMPTY;
  const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return name || EMPTY;
};

// Come `isoWithOffset` dell'app: data e ora locali con l'offset del fuso
// ("2026-10-19T08:05:00+02:00"), che il server converte in ora italiana.
export const isoWithOffset = (value: Date): string => {
  const offset = -value.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const minutes = Math.abs(offset);
  return (
    `${String(value.getFullYear()).padStart(4, '0')}-${pad(
      value.getMonth() + 1,
    )}-${pad(value.getDate())}` +
    `T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(
      value.getSeconds(),
    )}` +
    `${sign}${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
  );
};

// Data "YYYY-MM-DD" e ora "HH:mm" dei campi del form -> istante con offset.
// Null se uno dei due manca o non è valido.
export const dateTimeToIso = (
  date: string | null | undefined,
  time: string | null | undefined,
): string | null => {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  const t = /^(\d{2}):(\d{2})$/.exec(time ?? '');
  if (!d || !t) return null;
  const value = new Date(
    Number(d[1]),
    Number(d[2]) - 1,
    Number(d[3]),
    Number(t[1]),
    Number(t[2]),
  );
  return isNaN(value.getTime()) ? null : isoWithOffset(value);
};

// Importo digitato ("12,5", "1234.56") -> "12.50" per l'API, o null se non è
// un importo di al più due decimali. Lavora sui centesimi interi.
export const moneyToApi = (text: string | null | undefined): string | null => {
  const match = /^(\d{1,8})(?:[.,](\d{1,2}))?$/.exec((text ?? '').trim());
  if (!match) return null;
  const cents =
    Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
};
